import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Spinner, Table } from 'react-bootstrap';
import { ArrowLeft, CheckCircle, Clock, FileText, Landmark, Send, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ClaimedHoursService from '../../service/ClaimedHoursService';
import { minutosATexto } from '../../utils/hoursUtils';
import '../../styles/view/ClaimHours.css';

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.message) return data.message;
  if (data.title) return data.title;
  if (data.errors) {
    const first = Object.values(data.errors)[0];
    return Array.isArray(first) ? first[0] : first;
  }
  return fallback;
}

function EstadoBadge({ estado }) {
  if (estado === 'Aprobado') {
    return (
      <Badge bg="success">
        <CheckCircle size={12} className="me-1" />
        Aprobado
      </Badge>
    );
  }
  if (estado === 'Rechazado') {
    return (
      <Badge bg="danger">
        <XCircle size={12} className="me-1" />
        Rechazado
      </Badge>
    );
  }
  return (
    <Badge bg="warning" text="dark">
      <Clock size={12} className="me-1" />
      Pendiente
    </Badge>
  );
}

export default function ClaimHours() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [available, setAvailable] = useState(0);
  const [horas, setHoras] = useState('');
  const [minutos, setMinutos] = useState('');
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const requestedMinutes = useMemo(() => {
    const h = parseInt(horas, 10) || 0;
    const m = parseInt(minutos, 10) || 0;
    return h * 60 + m;
  }, [horas, minutos]);

  const loadData = async () => {
    try {
      setError('');
      const [availableData, claimsData] = await Promise.all([
        ClaimedHoursService.getAvailableClaimHours(user?.id),
        ClaimedHoursService.getMyClaims(user?.id),
      ]);
      setAvailable(availableData.horasDisponibles || 0);
      setClaims(claimsData);
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo cargar la información de reclamos.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setHoras('');
    setMinutos('');
    setComentario('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const h = parseInt(horas, 10);
    const m = parseInt(minutos, 10);

    if (Number.isNaN(h) || h < 0) {
      setError('Las horas deben ser un valor numérico mayor o igual a 0.');
      return;
    }
    if (Number.isNaN(m) || m < 0 || m > 59) {
      setError('Los minutos deben estar entre 0 y 59.');
      return;
    }
    if (requestedMinutes <= 0) {
      setError('Debes solicitar al menos 1 minuto.');
      return;
    }
    if (requestedMinutes > available) {
      setError('No tienes horas suficientes para esta solicitud.');
      return;
    }

    setSubmitting(true);
    try {
      await ClaimedHoursService.createClaim({
        usuarioId: user?.id,
        horasSolicitadas: requestedMinutes,
        comentario: comentario.trim() || null,
      });

      setSuccess('Solicitud enviada correctamente.');
      resetForm();
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo crear la solicitud de retiro.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '45vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container py-3 py-md-4 ch-root">
      <div className="mb-3">
        <Link to="/" className="text-decoration-none d-flex align-items-center text-muted">
          <ArrowLeft size={16} className="me-1" />
          Volver a Mis Horas
        </Link>
      </div>

      <div className="d-flex flex-column flex-lg-row gap-3 mb-4">
        <Card className="border-0 shadow-sm flex-fill">
          <Card.Body className="p-3 p-md-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h5 className="mb-1">Saldo disponible para retiro</h5>
                <small className="text-muted">Horas aprobadas menos reclamos aprobados</small>
              </div>
              <Landmark size={22} className="text-primary" />
            </div>
            <h2 className="mb-0 fw-bold">{minutosATexto(available)}</h2>
          </Card.Body>
        </Card>
      </div>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="row g-3 g-md-4">
        <div className="col-12 col-lg-5">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-3 p-md-4">
              <h5 className="mb-3">Solicitar retiro</h5>
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-medium">Horas</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={horas}
                      onChange={(e) => setHoras(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-medium">Minutos</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      max={59}
                      value={minutos}
                      onChange={(e) => setMinutos(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="mt-3 p-2 rounded ch-preview">
                  <small className="text-muted d-block mb-1">Solicitarás</small>
                  <strong>{minutosATexto(requestedMinutes)}</strong>
                </div>

                <div className="mt-3">
                  <label className="form-label fw-medium d-flex align-items-center gap-2">
                    <FileText size={16} />
                    Comentario
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    maxLength={500}
                    placeholder="Opcional"
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                  />
                </div>

                <button type="submit" className="btn btn-primary mt-3 w-100" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="me-2" />
                      Crear solicitud
                    </>
                  )}
                </button>
              </form>
            </Card.Body>
          </Card>
        </div>

        <div className="col-12 col-lg-7">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Historial de solicitudes</h5>
                <Badge bg="secondary">{claims.length}</Badge>
              </div>
              {claims.length === 0 ? (
                <p className="text-muted mb-0">Aún no tienes solicitudes de retiro.</p>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Fecha</th>
                        <th>Solicitado</th>
                        <th>Estado</th>
                        <th>Comentario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claims.map((claim) => (
                        <tr key={claim.id}>
                          <td>{claim.fechaSolicitud ? new Date(claim.fechaSolicitud).toLocaleDateString('es-ES') : '—'}</td>
                          <td className="fw-semibold">{minutosATexto(claim.horasSolicitadas)}</td>
                          <td><EstadoBadge estado={claim.estado} /></td>
                          <td className="small text-muted">
                            {claim.comentarioResolucion || claim.comentario || <span className="opacity-50">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}
